import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm() {
  const [form, setForm] = useState({ name: "", business_name: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [serverError, setServerError] = useState("");

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: "" }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (form.business_name.trim().length < 2) er.business_name = "Please enter your business name.";
    if (form.phone.replace(/\D/g, "").length < 7) er.phone = "Please enter a valid phone number.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const er = {};
        detail.forEach((d) => {
          const field = d.loc?.[d.loc.length - 1];
          if (field) er[field] = d.msg?.replace("Value error, ", "");
        });
        setErrors(er);
        setStatus("idle");
      } else {
        setServerError("Something went wrong. Please try again.");
        setStatus("error");
      }
    }
  };

  const reset = () => {
    setForm({ name: "", business_name: "", phone: "" });
    setStatus("idle");
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-pearl py-24 md:py-32">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-accent/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-accent-light/10 blur-[120px]" />
      </div>

      <div className="container-x">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* copy */}
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="eyebrow">Send An Enquiry</span>
            <h2 className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight text-ink md:text-6xl">
              Stop funding your competitors.
              <br />
              <span className="grad-text">Experience the follow-up.</span>
            </h2>
            <p className="mt-5 max-w-md text-lg text-ink-soft">
              Drop your details and we shall book you a demo.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "We call you in order to know your needs",
                "A specialist reaches out to book your demo",
                "No spam, ever — just a real conversation",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-ink-soft">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-dark">
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* glassmorphism form card */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.97 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2rem] border border-white/60 bg-white/70 p-7 shadow-soft backdrop-blur-xl md:p-10"
          >
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  data-testid="form-success"
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-8 text-center"
                >
                  <motion.span
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-accent text-white shadow-glow"
                  >
                    <Check size={42} strokeWidth={3} />
                  </motion.span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-ink">Thank you for your interest!</h3>
                  <p className="mt-2 max-w-xs text-ink-soft">
                    We’ve received your details — we’ll contact you for our services shortly.
                  </p>
                  <div className="mt-5 flex items-center gap-2 rounded-full bg-accent-soft px-4 py-2 text-sm font-semibold text-accent-dark">
                    <Check size={15} /> Our team will be in touch
                  </div>
                  <button onClick={reset} className="mt-6 text-sm font-medium text-ink-muted underline underline-offset-4 hover:text-accent">
                    Submit another
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  <Field id="name" label="Name" value={form.name} onChange={update("name")} error={errors.name} testid="form-name" />
                  <Field id="business_name" label="Business Name" value={form.business_name} onChange={update("business_name")} error={errors.business_name} testid="form-business" />
                  <Field id="phone" label="Phone Number" value={form.phone} onChange={update("phone")} error={errors.phone} testid="form-phone" type="tel" />

                  {serverError && <p className="text-sm font-medium text-red-500">{serverError}</p>}

                  <motion.button
                    data-testid="form-submit"
                    type="submit"
                    disabled={status === "loading"}
                    whileHover={status !== "loading" ? { scale: 1.02 } : {}}
                    whileTap={status !== "loading" ? { scale: 0.98 } : {}}
                    animate={status !== "loading" ? { boxShadow: ["0 10px 30px -10px rgba(79,70,229,0.5)", "0 18px 45px -8px rgba(79,70,229,0.7)", "0 10px 30px -10px rgba(79,70,229,0.5)"] } : {}}
                    transition={{ boxShadow: { duration: 2.2, repeat: Infinity, ease: "easeInOut" }, scale: { type: "spring", stiffness: 350, damping: 16 } }}
                    className="btn-pill mt-2 w-full bg-accent px-6 py-4 text-lg text-white disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <><Loader2 size={20} className="animate-spin" /> Sending…</>
                    ) : (
                      <><Send size={20} /> I’m Interested</>
                    )}
                  </motion.button>
                  <p className="text-center text-xs text-ink-muted">No spam. We’ll only use this to contact you about our services.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, error, testid, type = "text" }) {
  return (
    <div>
      <div className="float-field">
        <input
          id={id}
          data-testid={testid}
          type={type}
          placeholder=" "
          value={value}
          onChange={onChange}
          className={error ? "has-error" : ""}
          autoComplete="off"
        />
        <label htmlFor={id}>{label}</label>
      </div>
      {error && <p className="mt-1.5 pl-1 text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}
